import { useCallback, useMemo } from "react";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import type { SidebarThreadSummary } from "../types";

import { toastManager } from "../components/ui/toast";
import type { DestructiveConfirmHandle } from "../components/sidebar/useDestructiveConfirm";
import type { useThreadActions } from "./useThreadActions";

/**
 * Moving a thread on and off the archived shelf, with a failure the user sees.
 *
 * Both directions are here because both menus that offer them are built by the
 * same builder from the same slot: a thread is archived or it is not, so
 * `archive` and `unarchive` are one item, and the two surfaces that show it
 * should not each write their own error handling for half of it.
 *
 * The underlying mutations reject rather than reporting; without this a failed
 * archive is a row that silently does not move.
 *
 * @module useThreadShelfActions
 */

export interface ThreadShelfActions {
  readonly archiveThreadWithCleanup: (
    threadRef: ScopedThreadRef,
    threadSnapshot?: SidebarThreadSummary,
    options?: { readonly force?: boolean },
  ) => Promise<boolean>;
  readonly attemptArchiveThread: (
    threadRef: ScopedThreadRef,
    threadSnapshot?: SidebarThreadSummary,
    options?: { readonly force?: boolean },
  ) => Promise<void>;
  readonly attemptUnarchiveThread: (
    threadRef: ScopedThreadRef,
    threadSnapshot?: SidebarThreadSummary,
  ) => Promise<void>;
  readonly attemptSettleThread: (
    threadRef: ScopedThreadRef,
    threadSnapshot?: SidebarThreadSummary,
  ) => Promise<void>;
  readonly attemptUnsettleThread: (
    threadRef: ScopedThreadRef,
    threadSnapshot?: SidebarThreadSummary,
  ) => Promise<void>;
}

export function useThreadShelfActions(input: {
  readonly archiveThread: ReturnType<typeof useThreadActions>["archiveThread"];
  /** Absent on surfaces that never show an archived thread. */
  readonly unarchiveThread?: ReturnType<typeof useThreadActions>["unarchiveThread"] | undefined;
  readonly settleThread: ReturnType<typeof useThreadActions>["settleThread"];
  readonly confirmDestructiveAction: DestructiveConfirmHandle["confirm"];
  /** Absent on surfaces that never show a settled thread. */
  readonly unsettleThread?: ReturnType<typeof useThreadActions>["unsettleThread"] | undefined;
}): ThreadShelfActions {
  const { archiveThread, unarchiveThread, settleThread, unsettleThread, confirmDestructiveAction } =
    input;

  const archiveThreadWithCleanup = useCallback(
    (
      threadRef: ScopedThreadRef,
      threadSnapshot?: SidebarThreadSummary,
      options?: { readonly force?: boolean },
    ) =>
      archiveThread(threadRef, {
        ...(threadSnapshot ? { threadSnapshot } : {}),
        ...(options?.force === undefined ? {} : { force: options.force }),
      }),
    [archiveThread],
  );

  const attemptArchiveThread = useCallback(
    async (
      threadRef: ScopedThreadRef,
      threadSnapshot?: SidebarThreadSummary,
      options?: { readonly force?: boolean },
    ) => {
      try {
        const needsForceConfirmation =
          threadSnapshot?.archive?.state === "failed" &&
          threadSnapshot.archive.failureCode === "WORKTREE_DIRTY" &&
          options?.force !== true;
        if (needsForceConfirmation) {
          const confirmed = await confirmDestructiveAction({
            title: "Force archive dirty worktree?",
            description: `Force archiving "${threadSnapshot.title}" discards uncommitted work in its worktree. Thread history remains in CMMD.`,
            confirmLabel: "Force archive",
          });
          if (!confirmed) return;
          await archiveThreadWithCleanup(threadRef, threadSnapshot, { force: true });
          return;
        }
        await archiveThreadWithCleanup(threadRef, threadSnapshot, options);
      } catch (error) {
        toastManager.add({
          type: "error",
          title: "Failed to archive thread",
          description: error instanceof Error ? error.message : "An error occurred.",
        });
      }
    },
    [archiveThreadWithCleanup, confirmDestructiveAction],
  );

  const attemptUnarchiveThread = useCallback(
    async (threadRef: ScopedThreadRef, threadSnapshot?: SidebarThreadSummary) => {
      // Unreachable unless the menu offered `unarchive`, which the builder
      // only does when this handler exists. A throw here would mean the two
      // have drifted, which is worth surfacing rather than swallowing.
      if (!unarchiveThread) throw new Error("unarchive was offered without a handler");
      try {
        await unarchiveThread(threadRef, threadSnapshot ? { threadSnapshot } : {});
      } catch (error) {
        toastManager.add({
          type: "error",
          title: "Failed to unarchive thread",
          description: error instanceof Error ? error.message : "An error occurred.",
        });
      }
    },
    [unarchiveThread],
  );

  const attemptSettleThread = useCallback(
    async (threadRef: ScopedThreadRef, threadSnapshot?: SidebarThreadSummary) => {
      try {
        await settleThread(threadRef, threadSnapshot ? { threadSnapshot } : {});
      } catch (error) {
        toastManager.add({
          type: "error",
          title: "Failed to settle thread",
          description: error instanceof Error ? error.message : "An error occurred.",
        });
      }
    },
    [settleThread],
  );

  const attemptUnsettleThread = useCallback(
    async (threadRef: ScopedThreadRef, threadSnapshot?: SidebarThreadSummary) => {
      // Unreachable unless the menu offered `unsettle`, which the builder
      // only does when this handler exists. A throw here would mean the two
      // have drifted, which is worth surfacing rather than swallowing.
      if (!unsettleThread) throw new Error("unsettle was offered without a handler");
      try {
        await unsettleThread(threadRef, threadSnapshot ? { threadSnapshot } : {});
      } catch (error) {
        toastManager.add({
          type: "error",
          title: "Failed to un-settle thread",
          description: error instanceof Error ? error.message : "An error occurred.",
        });
      }
    },
    [unsettleThread],
  );

  return useMemo(
    () => ({
      archiveThreadWithCleanup,
      attemptArchiveThread,
      attemptUnarchiveThread,
      attemptSettleThread,
      attemptUnsettleThread,
    }),
    [
      archiveThreadWithCleanup,
      attemptArchiveThread,
      attemptUnarchiveThread,
      attemptSettleThread,
      attemptUnsettleThread,
    ],
  );
}
